import type { Device } from '@studio/models/device';
import { Protocol, toProtocol } from '@studio/models/control';
import { isCalibrating } from '@studio/models/control/calibration';
import type { Proxy } from '#~/agent/types.js';
import { locateProxy } from '#~/agent/index.js';
import { StudioError } from '#~/error/index.js';

export type PresetAccess = 'unknown' | 'error' | 'supported' | 'unsupported';
export const PRESET_UNSUPPORTED = "Position presets aren't supported by this camera.";
export const PRESET_CHECKING = 'Checking camera capabilities…';
export const PRESET_CHECK_FAILED =
  "Iris couldn't finish checking this camera's capabilities. Check that it's connected and try again.";

// Read optional published flags before the converted room model loses their presence.
export const presetAccess = (proxy: Proxy | undefined, deviceId: Device['id']): PresetAccess => {
  if (!proxy || proxy.isStarted === false) return 'error';
  const capabilities = proxy.state.status?.device?.[deviceId]?.capabilities;
  if (!capabilities) return 'unknown';
  const uri = proxy.state.control?.studio?.device?.[deviceId]?.control?.uri;
  const protocol = toProtocol(uri?.split('://')[0]);
  // Metadata cannot rule out live native fallback; the owner qualifies absolute first.
  if (
    protocol === Protocol.VISCA_IP ||
    protocol === Protocol.VISCA_SERIAL ||
    protocol === Protocol.ONVIF
  )
    return 'supported';
  const states = (['pan', 'tilt', 'zoom'] as const).map((axis) => {
    const value = capabilities.movement?.vectors?.[axis];
    if (value?.enabled === false) return false;
    if (
      value?.enabled !== true ||
      typeof value.absoluteMove !== 'boolean' ||
      typeof value.getPosition !== 'boolean'
    )
      return undefined;
    return value.absoluteMove && value.getPosition;
  });
  if (states.some((value) => value === true)) return 'supported';
  if (capabilities.overview?.hasMovement === false || states.every((value) => value === false))
    return 'unsupported';
  return 'error';
};

export const canUsePreset = (deviceId: Device['id']): boolean => {
  const proxy = locateProxy(deviceId);
  return (
    presetAccess(proxy, deviceId) !== 'unsupported' &&
    !isCalibrating(proxy?.state.status?.device?.[deviceId]?.calibration?.status?.state)
  );
};

// Callers recheck after awaits; unknown/error never become capability denial.
export const assertPresetAllowed = (deviceId: Device['id']): void => {
  const proxy = locateProxy(deviceId);
  if (!proxy) throw new StudioError('Unable to use preset without a connected camera.');
  if (presetAccess(proxy, deviceId) === 'unsupported') throw new StudioError(PRESET_UNSUPPORTED);
  if (isCalibrating(proxy.state.status?.device?.[deviceId]?.calibration?.status?.state))
    throw new StudioError('You cannot use a position preset while the camera is calibrating.');
};

/** Tests shorten this so the timeout case does not wait the full bound. */
export const presetCheckWait = { timeoutMs: 5_000 };

// Devices with an action waiting on capability detection. A count, so two
// overlapping actions keep the tray's status up until both have settled.
const checking = new Map<Device['id'], number>();
const checkingListeners = new Set<() => void>();

const markChecking = (deviceId: Device['id'], delta: 1 | -1): void => {
  const count = (checking.get(deviceId) ?? 0) + delta;
  if (count > 0) checking.set(deviceId, count);
  else checking.delete(deviceId);
  for (const listener of checkingListeners) listener();
};

/** Snapshot read for useSyncExternalStore (a boolean, so stable between changes). */
export const isPresetChecking = (deviceId: Device['id']): boolean => checking.has(deviceId);

export const subscribePresetChecking = (listener: () => void): (() => void) => {
  checkingListeners.add(listener);
  return () => {
    checkingListeners.delete(listener);
  };
};

// Renderer code: a plain timer, no Node-only unref.
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Gate for an action that picks its path from the camera's capabilities (save,
 * recall, recapture). The encoder publishes them when it adds the device, so
 * 'unknown' means it has not finished: the action waits, bounded, while the
 * tray shows PRESET_CHECKING. Only a check that never finishes refuses, and
 * its message says so instead of claiming the camera lacks support.
 *
 * Per rule: absent-data-gates-permissively (ai.knowledge/global) — the idle
 * tray and canUsePreset still treat 'unknown' as usable; only the acting
 * operator waits for the answer.
 * Per rule: settle-every-terminal-branch (ai.knowledge/global) — the checking
 * mark is released on every exit, the timeout included.
 */
export const awaitPresetAllowed = async (deviceId: Device['id']): Promise<void> => {
  if (presetAccess(locateProxy(deviceId), deviceId) === 'unknown') {
    markChecking(deviceId, 1);
    try {
      const deadline = Date.now() + presetCheckWait.timeoutMs;
      // Polled like the native preset receipt: status changes on every tick
      // and the wait is bounded.
      while (presetAccess(locateProxy(deviceId), deviceId) === 'unknown') {
        if (Date.now() >= deadline) throw new StudioError(PRESET_CHECK_FAILED);
        await sleep(25);
      }
    } finally {
      markChecking(deviceId, -1);
    }
  }
  assertPresetAllowed(deviceId);
};
